/// <amd-module name="MarkdownEditor/Utilities/MarkdownToolbarHelper" />

export namespace MarkdownSyntax {
  export const Header1: string = "# ";
  export const Header2: string = "## ";
  export const Header3: string = "### ";
  export const Code: string = "`";
  export const BulletedList: string = "- ";
  export const NumberedList: string = "1. ";
  export const TaskList: string = "- [ ] ";
  export const Bold: string = "**";
  export const Italic: string = "_";
  export const Mention: string = "@";
  export const LinkWIT: string = "#";
  export const CodeBlock: string = "\n```\n";
}

export namespace MarkdownRegex {
  export const Header1: RegExp = /^#\ /;
  export const Header2: RegExp = /^##\ /;
  export const Header3: RegExp = /^###\ /;

  export const BulletedList: RegExp = /^\-\ /;
  export const NumberedList: RegExp = /^\d{1,9}\.\ /;
  export const TaskList: RegExp = /^\-\ \[\ \]\ /;
}

export const HeaderMarkdownRegexes: RegExp[] = [
  MarkdownRegex.Header1,
  MarkdownRegex.Header2,
  MarkdownRegex.Header3
];
export const ListMarkdownRegexes: RegExp[] = [
  MarkdownRegex.TaskList,
  MarkdownRegex.BulletedList,
  MarkdownRegex.NumberedList
];
export const AllowedAttachmentExtensions = [
  ".PNG",
  ".GIF",
  ".JPG",
  ".JPEG",
  ".DOCX",
  ".PPTX",
  ".XLSX",
  ".TXT",
  ".PDF",
  ".ZIP",
  ".GZ",
  ".LYR",
  ".MOV",
  ".MP4"
];

/**
 * Define an interface which is a subset of the HTMLTextArea needed for this implementation.
 * 
 * @NOTE: This allows testing to drive the code without needing HTML Elements.
 */
export interface IMarkdownTextArea {
    selectionStart: number;
    selectionEnd: number;
    value: string;
  
    focus(): void;
}

// tslint:disable-next-line:no-stateless-class
export class MarkdownToolbarHelper {
  /**
   * This function inserts numbered list at the beginning of each line
   * @param textArea : the text component the toolbar is attached to.
   * @param textChanged : callback supplied to the parent for some browsers
   */
  public static insertNumberedList(
    textArea: IMarkdownTextArea | null,
    textChanged?: (
      text: string,
      selectionStart: number,
      selectionEnd: number
    ) => void
  ): void {
    if (!textArea) {
      return;
    }

    /**
     * Hack: textArea.selectionEnd is not populated correctly until it has focus once.
     * https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8018707/
     */
    if (this.isEdge() || this.isIE()) {
      textArea.focus();
    }

    const currentText = textArea.value;
    let selectionStart = textArea.selectionStart;
    const selectionEnd = textArea.selectionEnd;

    // move the cursor to the start of the current line for things like lists and quotes
    textArea.selectionStart = selectionStart =
      currentText.slice(0, selectionStart).lastIndexOf("\n") + 1;

    const selectedText = currentText.substring(selectionStart, selectionEnd);
    let lines = selectedText.split("\n");

    lines = lines.map((l, index) => {
      // This condition is to avoid adding markdown text if no character is selected in last line
      if (!l && selectionStart !== selectionEnd) {
        return l;
      }

      const matchedLineListMarkdown: string = this._getMarkdownSyntaxIfPresent(
        l,
        ListMarkdownRegexes
      );
      const numberedListMatch = MarkdownRegex.NumberedList.exec(l);

      if (numberedListMatch) {
        return l.substring(numberedListMatch[0].length, l.length);
      } else if (matchedLineListMarkdown) {
        return `${index + 1}. ${l.substring(
          matchedLineListMarkdown.length,
          l.length
        )}`;
      } else {
        return `${index + 1}. ${l}`;
      }
    });
    const newText = lines.join("\n");
    textArea.focus();

    if (this.browserSupportsInsertText()) {
      // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
      // This will also trigger any events on the parent control as if the user typed in this text
      document.execCommand("insertText", false, newText);
    } else {
      const firstPart = currentText.substring(0, selectionStart);
      const endPart = currentText.substring(selectionEnd);
      const newCursorPosition = firstPart.length + newText.length;

      // otherwise, the parent control needs to deal with the new text
      if (textChanged) {
        textChanged(
          firstPart + newText + endPart,
          newCursorPosition,
          newCursorPosition
        );
      }
    }
  }

  /**
   * This function inserts the insertedString param to the beginning of the line the cursor points to
   * @param textArea : the text component the toolbar is attached to.
   * @param insertedString :  the string to be inserted at line start
   * @param textChanged : callback supplied to the parent for some browsers
   * @param shouldToggle : should toggle the insert and removal of the insertedString
   * @param shouldReplaceIfHeader : when set would replace the header styling of the line
   * @param shouldReplaceIfList : when set would replace the list styling of the line
   */
  public static insertTextAtStartOfEachLine(
    textArea: IMarkdownTextArea | null,
    insertedString: string,
    textChanged?: (
      text: string,
      selectionStart: number,
      selectionEnd: number
    ) => void,
    shouldToggle?: boolean,
    shouldReplaceIfHeader?: boolean,
    shouldReplaceIfList?: boolean
  ): void {
    if (!textArea) {
      return;
    }

    /**
     * Hack: textArea.selectionEnd is not populated correctly until it has focus once.
     * https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8018707/
     */
    if (this.isEdge() || this.isIE()) {
      textArea.focus();
    }

    const currentText = textArea.value;
    let selectionStart = textArea.selectionStart;
    const selectionEnd = textArea.selectionEnd;

    // move the cursor to the start of the current line for things like lists and quotes
    textArea.selectionStart = selectionStart =
      currentText.slice(0, selectionStart).lastIndexOf("\n") + 1;

    const selectedText = currentText.substring(selectionStart, selectionEnd);
    let lines = selectedText.split("\n");

    lines = lines.map(l => {
      // This condition is to avoid adding markdown text if no character is selected in last line
      if (!l && selectionStart !== selectionEnd) {
        return l;
      }

      const matchedLineHeaderMarkdown: string = this._getMarkdownSyntaxIfPresent(
        l,
        HeaderMarkdownRegexes
      );
      const matchedLineListMarkdown: string = this._getMarkdownSyntaxIfPresent(
        l,
        ListMarkdownRegexes
      );
      if (
        shouldToggle &&
        (insertedString === matchedLineHeaderMarkdown ||
          insertedString === matchedLineListMarkdown)
      ) {
        return l.substring(insertedString.length, l.length);
      } else if (shouldReplaceIfHeader && matchedLineHeaderMarkdown) {
        return (
          insertedString +
          l.substring(matchedLineHeaderMarkdown.length, l.length)
        );
      } else if (shouldReplaceIfList && matchedLineListMarkdown) {
        return (
          insertedString + l.substring(matchedLineListMarkdown.length, l.length)
        );
      } else {
        return insertedString + l;
      }
    });
    const newText = lines.join("\n");
    textArea.focus();

    if (this.browserSupportsInsertText()) {
      // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
      // This will also trigger any events on the parent control as if the user typed in this text
      document.execCommand("insertText", false, newText);
    } else {
      const firstPart = currentText.substring(0, selectionStart);
      const endPart = currentText.substring(selectionEnd);
      const newCursorPosition = firstPart.length + newText.length;

      // otherwise, the parent control needs to deal with the new text
      if (textChanged) {
        textChanged(
          firstPart + newText + endPart,
          newCursorPosition,
          newCursorPosition
        );
      }
    }
  }

  /**
   * This function wraps the wrapperString param around the selected text.
   * @param textArea - the text component the toolbar is attached to.
   * @param wrapperString - the string to be wrapped around the selected text.
   * @param textChanged - callback supplied to the parent for some browsers
   */
  public static wrapText(
    textArea: IMarkdownTextArea | null,
    wrapperString: string,
    textChanged?: (
      text: string | null,
      selectionStart: number,
      selectionEnd: number
    ) => void
  ): void {
    if (!textArea) {
      return;
    }

    let cursorPosition = textArea.selectionStart;
    let selectionStart = textArea.selectionStart;
    let selectionEnd = textArea.selectionEnd;
    const currentText = textArea.value;
    const wrapperLength = wrapperString.length;
    const hasSelection = selectionStart !== selectionEnd;
    const trimmedCodeBlockString = MarkdownSyntax.CodeBlock.trim();
    const trimmedCodeBlockLength = trimmedCodeBlockString.length;

    // if some text is selected then we want to wrap the selected text minus outside whitespace
    // or if they have the cursor in the middle of the word, we want to wrap the word
    if (!hasSelection) {
      // expand from cursor position to create a selection
      const expandResults = this.expandCursorToWord(
        currentText,
        selectionStart
      );
      selectionStart = expandResults.start;
      selectionEnd = expandResults.end;
    } else {
      // if we do have a selection, trim beginning/trailing whitespace
      while (
        selectionStart < selectionEnd &&
        /\s/.test(currentText[selectionStart])
      ) {
        selectionStart += 1;
      }

      while (
        selectionEnd > selectionStart &&
        /\s/.test(currentText[selectionEnd - 1])
      ) {
        selectionEnd -= 1;
      }

      // if the selection has an incomplete wrapper string for toggling, remove the wrapper string selection
      if (wrapperString === MarkdownSyntax.CodeBlock) {
        if (
          currentText.substring(
            selectionStart,
            selectionStart + trimmedCodeBlockLength
          ) === trimmedCodeBlockString
        ) {
          selectionStart += trimmedCodeBlockLength + 1;
        }

        if (
          currentText.substring(
            selectionEnd - trimmedCodeBlockLength,
            selectionEnd
          ) === trimmedCodeBlockString
        ) {
          selectionEnd -= trimmedCodeBlockLength + 1;
        }
      }

      // then expand to grab the wrappers if they already exists
      if (
        selectionStart - wrapperLength >= 0 &&
        currentText.substr(selectionStart - wrapperLength, wrapperLength) ===
          wrapperString
      ) {
        selectionStart -= wrapperLength;
      }

      if (currentText.substr(selectionEnd, wrapperLength) === wrapperString) {
        selectionEnd += wrapperLength;
      }
    }

    const firstPart = currentText.substring(0, selectionStart);
    const endPart = currentText.substring(selectionEnd);
    const middlePart = currentText.substring(selectionStart, selectionEnd);

    let replacementText: string;

    // if the wrapper is already present, remove it. Otherwise, add it.
    // if you place your cursor over a word, for example, and press bold repeatidly, it should toggle bold for that word
    const alreadyMatches =
      wrapperString === middlePart.substr(0, wrapperLength) &&
      wrapperString === middlePart.substr(middlePart.length - wrapperLength);
    if (alreadyMatches) {
      replacementText = middlePart.substr(
        wrapperLength,
        middlePart.length - 2 * wrapperLength
      );
      cursorPosition -= wrapperLength;

      if (hasSelection) {
        selectionEnd -= 2 * wrapperLength;
      } else {
        selectionStart -= wrapperLength;
        selectionEnd -= wrapperLength;
      }
    } else {
      replacementText = wrapperString + middlePart + wrapperString;
      cursorPosition += wrapperLength;
      selectionStart += wrapperLength;
      selectionEnd += wrapperLength;
    }

    if (!hasSelection) {
      selectionStart = selectionEnd = cursorPosition;
    }

    textArea.focus();
    if (this.browserSupportsInsertText()) {
      // If the browser supports it, insertText is a better way to programatically change the text which supports undo/redo
      // This will also trigger any events on the parent control as if the user typed in this text
      textArea.selectionStart = firstPart.length;
      textArea.selectionEnd = firstPart.length + middlePart.length;

      document.execCommand("insertText", false, replacementText);

      if (textChanged) {
        textChanged(null, selectionStart, selectionEnd);
      }
    } else {
      // otherwise, the parent control needs to deal with the new text
      if (textChanged) {
        textChanged(
          firstPart + replacementText + endPart,
          selectionStart,
          selectionEnd
        );
      }
    }
  }

  /**
   * This function virtually selects the whole word the cursor is in.
   * @param text - the text present in the textarea component
   * @param cursorPosition
   */
  public static expandCursorToWord(
    text: string,
    cursorPosition: number
  ): SelectionRange {
    const left = text.slice(0, cursorPosition + 1).search(/\S+$/);
    const right = text.slice(cursorPosition).search(/\s/);

    let start = left < 0 ? cursorPosition : left;
    let end = right < 0 ? text.length : right + cursorPosition;

    start = Math.min(Math.max(0, start), text.length);
    end = Math.min(Math.max(0, end), text.length);

    return { start: start, end: end };
  }

  /**
   * Tests whether insertText is supported in the browser.
   * insertText programatically programatically change the text which supports undo/redo
   */
  public static browserSupportsInsertText(): boolean {
    // ie does not support it and correctly reports that it does not support it.
    // firefox claims to support it... but lies.
    // see https://bugzilla.mozilla.org/show_bug.cgi?id=1220696
    if (this.isFirefox() || !document.queryCommandSupported("insertText")) {
      return false;
    }

    return true;
  }

  public static isFirefox(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const mozilla = /mozilla.*rv:([\d+.]+)/i.exec(navigator.userAgent);

    return !!mozilla && userAgent.indexOf("trident") === -1;
  }

  public static isEdge(): boolean {
    const edge = /edge\/([\d+.]+)/i.exec(navigator.userAgent);
    return !!edge;
  }

  public static isIE(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const msie = /msie ([\d+.]+)/i.exec(navigator.userAgent);

    return !!msie || userAgent.indexOf("trident") !== -1;
  }

  /**
   * Gets markdown syntax if present in the beginning of the line from given list of markdown syntaxes
   * @param {string} line - where to search markdown syntax
   * @param {string[]} markdownSyntaxesList - list from which markdown syntaxes are searched in given line
   * @returns - markdown syntax matched (from beginning of line) from given list of syntaxes or null otherwise
   */
  private static _getMarkdownSyntaxIfPresent(
    line: string,
    markdownRegexesList: RegExp[]
  ): string {
    for (const markdownRegex of markdownRegexesList) {
      const syntaxMatch = markdownRegex.exec(line);
      if (syntaxMatch) {
        return syntaxMatch[0];
      }
    }
    return "";
  }
}

export class SelectionRange {
  public start: number;
  public end: number;
}
